import { FC, useMemo } from 'react';

import { MdFileDownload } from 'react-icons/md';
import { IoEye, IoTrash } from 'react-icons/io5';
// import filesIcon from 'assets/images/Files.svg';
import { Tooltip } from 'ui/components/common';
import { Attachment } from 'utils/types';
import { SIZE } from 'utils/constants';
import { cn } from 'utils/helpers';
import { cva } from 'class-variance-authority';
import { FileIcon } from 'ui/components/icon';

const listItemClasses = cva(
  [
    'rounded-[6px] border-[0.5px] border-separator-primary',
    'relative bg-fill-secondary',
    'flex items-center justify-center',
  ],
  {
    variants: {
      size: {
        [SIZE.Small]: ['h-10 w-10 p-[1px]'],
        [SIZE.Medium]: ['h-[64px] w-[64px] p-[1px]'],
        [SIZE.Large]: ['h-[86px] w-[86px] p-[1px]'],
      },
    },
    defaultVariants: {
      size: SIZE.Small,
    },
  },
);

const contentClassNames = cva(
  [
    'absolute top-0',
    'bg-[#11334C60]',
    'rounded-[6px] opacity-0 hover:opacity-100',
    'flex items-center justify-center',
  ],
  {
    variants: {
      size: {
        [SIZE.Small]: ['h-10 w-10 p-[1px]'],
        [SIZE.Medium]: ['h-[64px] w-[64px] p-[1px]'],
        [SIZE.Large]: ['h-[85px] w-[85px] p-[1px]'],
      },
    },
    defaultVariants: {
      size: SIZE.Large,
    },
  },
);

interface PROPS {
  file: Attachment;
  size?: SIZE.Small | SIZE.Medium | SIZE.Large;
  onDownload?: () => void;
  showPreview?: () => void;
  onRemove?: (attachmentId: string) => void;
  showRemove?: boolean;
}

const AttachedListItem: FC<PROPS> = ({
  size,
  file,
  onDownload,
  showPreview,
  showRemove,
  onRemove,
}) => {
  const dimensions = size === SIZE.Large ? 85 : 64;

  const iconsToShow = useMemo(() => {
    if (showRemove) {
      return (
        <div className={cn(contentClassNames({ size }))}>
          <div
            className={cn(
              'rounded-full py-2',
              'h-full w-full',
              'flex justify-center',
            )}
          >
            <button
              onClick={() => onRemove?.(file._id as string)}
              className={cn(
                'rounded-full',
                'grid',
                'place-items-center',
                'w-10',
                'h-10',
                'bg-secondary-lightest',
              )}
            >
              <IoTrash />
            </button>
          </div>
        </div>
      );
    }

    if (onDownload) {
      return (
        <div className={cn(contentClassNames({ size }))}>
          <div
            className={cn(
              'rounded-full py-2',
              'h-full w-full',
              'flex items-end justify-center gap-1',
            )}
          >
            <button
              onClick={showPreview}
              className={cn(
                'rounded-full',
                'grid',
                'place-items-center',
                'w-6',
                'h-6',
                'bg-secondary-lightest',
              )}
            >
              <IoEye />
            </button>

            <button
              onClick={onDownload}
              className="rounded-full grid place-items-center w-6 h-6 bg-info-base"
            >
              <MdFileDownload className="text-fill-tertiary" />
            </button>
          </div>
        </div>
      );
    }
  }, [file._id, onDownload, onRemove, showPreview, showRemove, size]);
  return (
    <Tooltip
      strategy="fixed"
      title={file.fileName}
      placement="bottom"
      tooltipContainerClassName="max-w-none"
    >
      <div className={cn(listItemClasses({ size }))}>
        {file.fileThumbnail ? (
          <img
            src={`${file.fileThumbnail}-/preview/${dimensions}x${dimensions}/`}
            alt={file.fileName}
            className="h-full w-full rounded-[6px] object-contain"
          />
        ) : (
          <div className="bg-fill-primary rounded-md grid place-items-center">
            <FileIcon className="w-7 h-7 text-content" />
          </div>
        )}
        {iconsToShow}
      </div>
    </Tooltip>
  );
};

export default AttachedListItem;
